import { useState, useEffect } from "react";
import { useTheme } from "@/hooks/useTheme";

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const { isDark, toggleTheme } = useTheme();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollTo = (id: string) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth" });
    setMenuOpen(false);
  };

  const navBg = scrolled
    ? isDark
      ? "rgba(28,26,34,0.92)"
      : "rgba(252,248,242,0.92)"
    : "transparent";

  const borderColor = scrolled
    ? isDark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.08)"
    : "transparent";

  const textColor = isDark ? "rgba(255,255,255,0.75)" : "rgba(30,25,20,0.75)";
  const textHover = isDark ? "#ffffff" : "#1C1A22";
  const mobileMenuBg = isDark ? "rgba(28,26,34,0.98)" : "rgba(252,248,242,0.98)";

  return (
    <nav
      className="fixed top-0 left-0 right-0 z-50 transition-all duration-500 backdrop-blur-xl"
      style={{ background: navBg, borderBottom: `1px solid ${borderColor}` }}
    >
      <div className="mx-auto px-6 md:px-12 flex items-center justify-between" style={{ height: "72px" }}>
        {/* Logo */}
        <button onClick={() => scrollTo("home")} className="flex items-center cursor-pointer">
          <img
            src="https://public.readdy.ai/ai/img_res/abee19c3-0782-4d36-ae83-20a2d803b3c1.png"
            alt="Automation Integration AI Logo"
            className="h-12 w-auto object-contain transition-all duration-300"
            style={{
              filter: isDark
                ? "drop-shadow(0 0 10px rgba(232,118,26,0.25))"
                : "drop-shadow(0 0 6px rgba(232,118,26,0.2)) brightness(0.92)",
            }}
          />
        </button>

        {/* Desktop Nav */}
        <div className="hidden md:flex items-center gap-8">
          {[
            { label: "Home", id: "home" },
            { label: "Industries", id: "industries" },
            { label: "Results", id: "case-studies" },
            { label: "Contact", id: "contact" },
          ].map((item) => (
            <button
              key={item.id}
              onClick={() => scrollTo(item.id)}
              className="text-[13px] transition-colors duration-200 cursor-pointer whitespace-nowrap font-light tracking-wide"
              style={{ color: textColor }}
              onMouseEnter={(e) => ((e.currentTarget as HTMLElement).style.color = textHover)}
              onMouseLeave={(e) => ((e.currentTarget as HTMLElement).style.color = textColor)}
            >
              {item.label}
            </button>
          ))}

          {/* Theme Toggle */}
          <button
            onClick={toggleTheme}
            className="w-9 h-9 flex items-center justify-center rounded-full transition-all duration-300 cursor-pointer"
            style={{
              background: isDark ? "rgba(255,255,255,0.07)" : "rgba(0,0,0,0.06)",
              border: isDark ? "1px solid rgba(255,255,255,0.1)" : "1px solid rgba(0,0,0,0.1)",
              color: isDark ? "rgba(255,255,255,0.7)" : "rgba(30,25,20,0.7)",
            }}
            title={isDark ? "Switch to Light Mode" : "Switch to Dark Mode"}
          >
            <i className={isDark ? "ri-sun-line text-sm" : "ri-moon-line text-sm"}></i>
          </button>

          <button
            onClick={() => scrollTo("book-audit")}
            className="ml-1 px-5 py-2 rounded-full text-white text-[13px] font-semibold transition-all duration-200 cursor-pointer whitespace-nowrap"
            style={{ background: "#E8761A" }}
            onMouseEnter={(e) => ((e.currentTarget as HTMLElement).style.background = "#d4681a")}
            onMouseLeave={(e) => ((e.currentTarget as HTMLElement).style.background = "#E8761A")}
          >
            Book Audit
          </button>
        </div>

        {/* Mobile right side */}
        <div className="md:hidden flex items-center gap-3">
          <button
            onClick={toggleTheme}
            className="w-8 h-8 flex items-center justify-center rounded-full cursor-pointer"
            style={{
              background: isDark ? "rgba(255,255,255,0.07)" : "rgba(0,0,0,0.06)",
              color: isDark ? "rgba(255,255,255,0.7)" : "rgba(30,25,20,0.7)",
            }}
          >
            <i className={isDark ? "ri-sun-line text-sm" : "ri-moon-line text-sm"}></i>
          </button>
          <button
            className="w-8 h-8 flex items-center justify-center cursor-pointer"
            style={{ color: isDark ? "white" : "#1C1A22" }}
            onClick={() => setMenuOpen(!menuOpen)}
          >
            <i className={menuOpen ? "ri-close-line text-xl" : "ri-menu-line text-xl"}></i>
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {menuOpen && (
        <div
          className="md:hidden backdrop-blur-xl border-t px-6 py-6 flex flex-col gap-5"
          style={{
            background: mobileMenuBg,
            borderColor: isDark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.08)",
          }}
        >
          {[
            { label: "Home", id: "home" },
            { label: "Industries", id: "industries" },
            { label: "Results", id: "case-studies" },
            { label: "Contact", id: "contact" },
          ].map((item) => (
            <button
              key={item.id}
              onClick={() => scrollTo(item.id)}
              className="text-sm text-left cursor-pointer font-light"
              style={{ color: isDark ? "rgba(255,255,255,0.7)" : "rgba(30,25,20,0.7)" }}
            >
              {item.label}
            </button>
          ))}
          <button
            onClick={() => scrollTo("book-audit")}
            className="mt-2 px-5 py-2.5 rounded-full text-white text-sm font-semibold text-center cursor-pointer"
            style={{ background: "#E8761A" }}
          >
            Book Audit
          </button>
        </div>
      )}
    </nav>
  );
}
